import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash, FacebookLogo, InstagramLogo, GoogleLogo, TiktokLogo, PaperPlaneTilt, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";

const platformIcon = { facebook: FacebookLogo, instagram: InstagramLogo, google: GoogleLogo, tiktok: TiktokLogo };

export default function CalendarPage() {
  const [posts, setPosts] = useState([]);
  const [date, setDate] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ platform: "facebook", content: "", scheduled_for: new Date().toISOString().slice(0,16), image_url: "" });

  const load = () => api.get("/posts").then(r => setPosts(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.content) return toast.error("Content required");
    try {
      await api.post("/posts", {
        platform: form.platform,
        content: form.content,
        image_url: form.image_url,
        scheduled_for: new Date(form.scheduled_for).toISOString(),
        status: "scheduled",
      });
      toast.success("Post scheduled");
      setOpen(false);
      setForm({ platform: "facebook", content: "", scheduled_for: new Date().toISOString().slice(0,16), image_url: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => { await api.delete(`/posts/${id}`); load(); };
  const publish = async (id) => {
    try {
      const r = await api.post(`/posts/${id}/publish`);
      if (r.data.status === "posted") toast.success("Published");
      else toast.error(r.data.result?.reason || "Publish failed - check Connections");
      load();
    } catch { toast.error("Publish failed"); }
  };

  const postsForDate = posts.filter(p => format(new Date(p.scheduled_for), "yyyy-MM-dd") === format(date, "yyyy-MM-dd"));
  const datesWithPosts = posts.map(p => new Date(p.scheduled_for));

  return (
    <div data-testid="calendar-page">
      <PageHeader
        eyebrow="Content Calendar"
        title="Plan & schedule your social posts."
        description="Build a consistent posting cadence across Facebook, Google, Instagram and TikTok."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-post" className="bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11">
                <Plus className="mr-2" size={18} />Schedule a post
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg">
              <DialogHeader><DialogTitle className="font-display">New scheduled post</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Platform</Label>
                  <Select value={form.platform} onValueChange={(v)=>setForm({...form, platform: v})}>
                    <SelectTrigger data-testid="form-platform" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="google">Google Business</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Content</Label>
                  <Textarea data-testid="form-content" rows={4} value={form.content} onChange={(e)=>setForm({...form, content: e.target.value})} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Schedule</Label>
                  <Input data-testid="form-when" type="datetime-local" value={form.scheduled_for} onChange={(e)=>setForm({...form, scheduled_for: e.target.value})} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Image URL (optional)</Label>
                  <Input data-testid="form-image" value={form.image_url} onChange={(e)=>setForm({...form, image_url: e.target.value})} className="rounded-sm mt-1" />
                </div>
                <Button data-testid="btn-submit-post" onClick={submit} className="w-full bg-slate-900 text-white rounded-sm h-11">Schedule</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="sharp-card p-6">
          <Calendar mode="single" selected={date} onSelect={(d)=>d && setDate(d)} modifiers={{ has: datesWithPosts }} modifiersClassNames={{ has: "relative font-bold text-slate-900 after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:bg-navy after:rounded-full" }} className="rounded-sm" />
          <div className="mt-4 text-xs text-slate-500">Yellow dot = scheduled posts</div>
        </Card>
        <Card className="sharp-card p-6 lg:col-span-2">
          <div className="eyebrow mb-3">{format(date, "EEEE, MMMM d")}</div>
          {postsForDate.length === 0 ? (
            <div className="text-sm text-slate-400 py-10 text-center">No posts scheduled for this day.</div>
          ) : (
            <div className="space-y-3">
              {postsForDate.map((p) => {
                const Icon = platformIcon[p.platform] || FacebookLogo;
                return (
                  <div key={p.id} data-testid={`post-${p.id}`} className="border border-slate-200 rounded-sm p-4 flex gap-4">
                    {p.image_url && <img src={p.image_url} alt="" className="w-16 h-16 object-cover rounded-sm" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={14} weight="fill" className="text-slate-700" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">{p.platform}</span>
                        <span className="text-xs text-slate-400">· {format(new Date(p.scheduled_for), "p")}</span>
                        {p.status === "posted" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-600"><CheckCircle size={10} weight="fill" />Posted</span>}
                        {p.status === "failed" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-red-600"><XCircle size={10} weight="fill" />Failed</span>}
                      </div>
                      <p className="text-sm text-slate-800 line-clamp-3">{p.content}</p>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {p.status === "scheduled" && (
                        <button data-testid={`pub-${p.id}`} onClick={()=>publish(p.id)} title="Publish now" className="text-slate-400 hover:text-navy transition-colors"><PaperPlaneTilt size={16} /></button>
                      )}
                      <button data-testid={`del-${p.id}`} onClick={()=>remove(p.id)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash size={16} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
